/**
 * Facebook Pixel helper — safe wrapper around window.fbq()
 * Pixel ID: 4121397994857691 (initialized in /public/index.html)
 *
 * Usage:
 *   import { fbTrack, fbTrackPurchase } from '@/lib/fbpixel';
 *   fbTrack('InitiateCheckout', { value: 499, currency: 'INR' });
 *   fbTrackPurchase({ value: 499, currency: 'INR', content_name: 'Basic Plan' });
 */

const PIXEL_ID = '4121397994857691';
const SITE_CURRENCY = 'INR';

/** Check if fbq is available (Pixel loaded). */
const isReady = () => typeof window !== 'undefined' && typeof window.fbq === 'function';

/** Track a standard Meta Pixel event. */
export const fbTrack = (eventName, params = {}) => {
  try {
    if (!isReady()) return;
    if (params && Object.keys(params).length > 0) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('track', eventName);
    }
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[FB Pixel]', eventName, params);
    }
  } catch (e) {
    // silently ignore
  }
};

/** Track a custom event (non-standard event name). */
export const fbTrackCustom = (eventName, params = {}) => {
  try {
    if (!isReady()) return;
    window.fbq('trackCustom', eventName, params);
  } catch (e) {
    // silently ignore
  }
};

/* ============================================================
 * High-level helpers for the most important events
 * ============================================================ */

/** Page view (fired once on first load by Pixel base code; this is for SPA route changes). */
export const fbPageView = () => fbTrack('PageView');

/** User viewed product/package details. */
export const fbViewContent = ({ content_name, content_ids = [], value, content_type = 'product' } = {}) => {
  const params = { content_type };
  if (content_name) params.content_name = content_name;
  if (content_ids && content_ids.length) params.content_ids = content_ids;
  if (value !== undefined && value !== null) {
    params.value = Number(value) || 0;
    params.currency = SITE_CURRENCY;
  }
  fbTrack('ViewContent', params);
};

/** User clicked "Buy / Proceed to checkout". */
export const fbInitiateCheckout = ({ value, content_name, content_ids = [], num_items = 1 } = {}) => {
  const params = { num_items };
  if (content_name) params.content_name = content_name;
  if (content_ids && content_ids.length) params.content_ids = content_ids;
  if (value !== undefined && value !== null) {
    params.value = Number(value) || 0;
    params.currency = SITE_CURRENCY;
  }
  fbTrack('InitiateCheckout', params);
};

/** User filled payment info / chose payment method. */
export const fbAddPaymentInfo = ({ value, content_name } = {}) => {
  const params = {};
  if (content_name) params.content_name = content_name;
  if (value !== undefined && value !== null) {
    params.value = Number(value) || 0;
    params.currency = SITE_CURRENCY;
  }
  fbTrack('AddPaymentInfo', params);
};

/** Purchase completed (with value & currency — MOST IMPORTANT for ads optimization). */
export const fbTrackPurchase = ({ value, content_name, content_ids = [], transaction_id } = {}) => {
  const params = {
    value: Number(value) || 0,
    currency: SITE_CURRENCY,
  };
  if (content_name) params.content_name = content_name;
  if (content_ids && content_ids.length) params.content_ids = content_ids;
  if (transaction_id) params.transaction_id = transaction_id; // eventID-style dedup hint
  fbTrack('Purchase', params);
};

/** User submitted a lead/contact form. */
export const fbTrackLead = ({ content_name, value } = {}) => {
  const params = {};
  if (content_name) params.content_name = content_name;
  if (value !== undefined && value !== null) {
    params.value = Number(value) || 0;
    params.currency = SITE_CURRENCY;
  }
  fbTrack('Lead', params);
};

/** User completed registration / signup. */
export const fbTrackRegistration = ({ method = 'email', value } = {}) => {
  const params = {};
  if (method) params.content_name = method;
  if (value !== undefined && value !== null) {
    params.value = Number(value) || 0;
    params.currency = SITE_CURRENCY;
  }
  fbTrack('CompleteRegistration', params);
};

export { PIXEL_ID };
export default {
  fbTrack,
  fbTrackCustom,
  fbPageView,
  fbViewContent,
  fbInitiateCheckout,
  fbAddPaymentInfo,
  fbTrackPurchase,
  fbTrackLead,
  fbTrackRegistration,
  PIXEL_ID,
};
